import React, { useState, useEffect } from 'react';
import { IconButton, CircularProgress, Tooltip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import useAudioRecording from '../../utils/useAudioRecording';
import { useDispatch } from 'react-redux';
import { transcribeAudio } from '../../store/Assistant';
import { toast } from 'react-toastify';
import './AudioInput.css';

const AudioInput = ({ onTranscriptionComplete, disabled }) => {
  const dispatch = useDispatch();
  const [isTranscribing, setIsTranscribing] = useState(false);
  const {
    isRecording,
    audioBlob,
    error,
    startRecording,
    stopRecording,
    resetRecording,
    getAudioBase64
  } = useAudioRecording();

  // Handle transcription when audio is ready
  useEffect(() => {
    const handleTranscription = async () => {
      if (audioBlob && !isTranscribing) {
        setIsTranscribing(true);
        try {
          const audioBase64 = await getAudioBase64();

          // Dispatch transcription action
          dispatch(transcribeAudio({
            audio: audioBase64,
            onSuccess: (transcribedText) => {
              onTranscriptionComplete(transcribedText);
              resetRecording();
              setIsTranscribing(false);
            },
            onError: (error) => {
              toast.error(`Transcription failed: ${error}`);
              setIsTranscribing(false);
              resetRecording();
            }
          }));
        } catch (err) {
          toast.error('Failed to process audio');
          setIsTranscribing(false);
          resetRecording();
        }
      }
    };

    handleTranscription();
  }, [audioBlob, dispatch, getAudioBase64, isTranscribing, onTranscriptionComplete, resetRecording]);

  // Handle error
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className="audio-input-wrapper">
      <Tooltip title={isRecording ? 'Stop recording' : 'Start recording'}>
        <span>
          <IconButton
            onClick={handleClick}
            disabled={disabled || isTranscribing}
            className={`audio-input-button ${isRecording ? 'recording' : ''}`}
            size="small"
          >
            {isTranscribing ? (
              <CircularProgress size={20} />
            ) : isRecording ? (
               <i className='icon fa fa-stop'></i>
            ) : (
              <i className='icon'>
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.99935 5.41663C3.12602 5.41663 2.41602 4.70663 2.41602 3.83329V1.99996C2.41602 1.12663 3.12602 0.416626 3.99935 0.416626C4.87268 0.416626 5.58268 1.12663 5.58268 1.99996V3.83329C5.58268 4.70663 4.87268 5.41663 3.99935 5.41663ZM3.99935 0.916626C3.40268 0.916626 2.91602 1.40329 2.91602 1.99996V3.83329C2.91602 4.42996 3.40268 4.91663 3.99935 4.91663C4.59602 4.91663 5.08268 4.42996 5.08268 3.83329V1.99996C5.08268 1.40329 4.59602 0.916626 3.99935 0.916626Z" fill="#4B5563" />
                  <path d="M4.0002 6.58334C2.45686 6.58334 1.2002 5.32668 1.2002 3.78334V3.21667C1.2002 3.08001 1.31353 2.96667 1.4502 2.96667C1.58686 2.96667 1.7002 3.08001 1.7002 3.21667V3.78334C1.7002 5.05001 2.73353 6.08334 4.0002 6.08334C5.26686 6.08334 6.30019 5.05001 6.30019 3.78334V3.21667C6.30019 3.08001 6.41353 2.96667 6.55019 2.96667C6.68686 2.96667 6.80019 3.08001 6.80019 3.21667V3.78334C6.80019 5.32668 5.54353 6.58334 4.0002 6.58334Z" fill="#4B5563" />
                  <path d="M4.46297 2.39334C4.4363 2.39334 4.4063 2.39 4.3763 2.38C4.13297 2.29 3.8663 2.29 3.62297 2.38C3.49297 2.42667 3.34963 2.36 3.30297 2.23C3.2563 2.1 3.32297 1.95667 3.45297 1.91C3.8063 1.78334 4.1963 1.78334 4.54963 1.91C4.67963 1.95667 4.7463 2.1 4.69963 2.23C4.65963 2.33 4.56297 2.39334 4.46297 2.39334Z" fill="#4B5563" />
                  <path d="M4.26702 3.1C4.24368 3.1 4.22368 3.09667 4.20035 3.09C4.06702 3.05334 3.93035 3.05334 3.79702 3.09C3.66368 3.12667 3.52702 3.04667 3.49035 2.91334C3.45368 2.78334 3.53368 2.64667 3.66702 2.61C3.88368 2.55 4.11702 2.55 4.33368 2.61C4.46702 2.64667 4.54702 2.78334 4.51035 2.91667C4.48035 3.02667 4.37702 3.1 4.26702 3.1Z" fill="#4B5563" />
                  <path d="M4 7.58337C3.86333 7.58337 3.75 7.47004 3.75 7.33337V6.33337C3.75 6.19671 3.86333 6.08337 4 6.08337C4.13667 6.08337 4.25 6.19671 4.25 6.33337V7.33337C4.25 7.47004 4.13667 7.58337 4 7.58337Z" fill="#4B5563" />
                </svg>
              </i>
            )}
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
};

export default AudioInput;